import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  VStack,
  HStack,
  Text,
  Image,
  Checkbox,
  Button,
  Link,
  useToast,
} from "@chakra-ui/react";
import { pxToRem } from "../../utils/editor";
import { cancelReasons } from "../../utils/const";
import { PaymentService } from "../../services/paymentService";

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: number;
  setStep: (step: number) => void;
  selectedCancelReasons: string[];
  onToggleCancelReason: (reasonId: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  step,
  setStep,
  selectedCancelReasons,
  onToggleCancelReason,
  isLoading,
  setIsLoading,
}) => {
  const toast = useToast();

  const confirmCancellation = async () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      // 执行退款操作
      setIsLoading(true);
      try {
        const result = await PaymentService.refund();

        if (result?.success == true) {
          setStep(3);
          toast({
            title: "Refund Processed",
            description:
              result.message || "Refund has been processed successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Refund Error",
            description: result?.detail || "Failed to process refund",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("退款失败:", error);
        toast({
          title: "Refund Error",
          description: "Failed to process refund. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    } else if (step === 3) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay
        background={"blackAlpha.300"}
        backdropFilter={"blur(8px)"}
      />
      <ModalContent
        maxW={pxToRem(846)}
        borderRadius={pxToRem(24)}
        p={pxToRem(24)}
      >
        <ModalHeader
          color={"#333"}
          fontSize={pxToRem(32)}
          fontWeight="700"
          textAlign="left"
          p={0}
        >
          Cancel Subscription
        </ModalHeader>
        {step === 1 && (
          <Text
            fontSize={pxToRem(16)}
            color="333"
            textAlign="left"
            mt={pxToRem(6)}
          >
            Why are you canceling your subscription?
          </Text>
        )}
        <Image
          cursor={"pointer"}
          w={pxToRem(24)}
          position={"absolute"}
          top={pxToRem(20)}
          right={pxToRem(20)}
          src={"/static/payment/payment-close.svg"}
          onClick={() => onClose()}
        />
        <ModalBody>
          <VStack align="start" spacing={pxToRem(16)}>
            <VStack align="center" w="100%" h={pxToRem(230)}>
              {step === 1 && (
                <VStack align="start" spacing={pxToRem(16)}>
                  {cancelReasons.map((reason) => (
                    <Checkbox
                      key={reason.id}
                      isChecked={selectedCancelReasons.includes(reason.id)}
                      onChange={() => onToggleCancelReason(reason.id)}
                      colorScheme="blue"
                      size="lg"
                      ml={pxToRem(90)}
                    >
                      <Text margin={0} color="#000" fontSize={pxToRem(16)}>
                        {reason.label}
                      </Text>
                    </Checkbox>
                  ))}
                </VStack>
              )}

              {step === 2 && (
                <VStack align="center" h="100%" justifyContent={"center"}>
                  <Text margin={0} color="#333" fontSize={pxToRem(20)}>
                    Are you sure you want to cancel your subscription?
                  </Text>
                </VStack>
              )}

              {step === 3 && (
                <VStack align="center" h="100%" justifyContent={"center"}>
                  <Text
                    margin={0}
                    color="#333"
                    fontSize={pxToRem(20)}
                    fontWeight={"bold"}
                  >
                    Your subscription has been canceled. We're sad to see
                    you go!
                  </Text>
                  <Text margin={0} color="#333" fontSize={pxToRem(16)}>
                    If you have any refund request, please send email to&nbsp;
                    <Link color="blue.500" href="mailto:support@doenba.ai">
                      support@doenba.ai
                    </Link>
                  </Text>
                </VStack>
              )}
            </VStack>

            <HStack
              width="100%"
              justifyContent="space-between"
              mt={pxToRem(24)}
            >
              {/* 稍后按钮 */}
              <Button
                bgColor={"#1D8AFF"}
                color="#fff"
                borderRadius={pxToRem(12)}
                fontSize={pxToRem(24)}
                fontWeight="700"
                py={pxToRem(10)}
                px={pxToRem(48)}
                _hover={{
                  bg: "#1974E8",
                }}
                onClick={onClose}
              >
                Later
              </Button>

              {/* 下一步按钮 */}
              <Button
                bg="#D3D7E2"
                color="#333"
                borderRadius={pxToRem(12)}
                fontSize={pxToRem(24)}
                fontWeight="700"
                py={pxToRem(10)}
                px={pxToRem(48)}
                onClick={confirmCancellation}
                isDisabled={
                  step === 1 && selectedCancelReasons.length === 0
                }
                isLoading={isLoading}
              >
                {step === 3 ? "Done" : "Next"}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CancelSubscriptionModal;